import React, { useState, useEffect } from "react";
import {
  Button,
  Space,
  Popconfirm,
  message,
  Col,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import ShopServ from "../../service/ShopServ";
import CategoryServ from "../../service/CategoryServ";
import AddShop from "../../component/AddShop";
import TextWithPopover from "../../util/TextWithPopover";
import { getImageUrl } from "../../component/SafeImage";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import FormSelect from "../../component/ui/FormSelect";
import { ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import Breadcrumbs from "../../component/Breadcrumbs";
import TableImage from "../../component/ui/TableImage";

const ShopSetting = () => {
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [categories, setCategories] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addShop, setAddShop] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [shopDetails, setShopDetails] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalShops, setTotalShops] = useState(0);

  // ✅ Real API fetch with role-based filtering (handled by backend)
  const fetchShops = async () => {
    try {
      setLoading(true);
      const params = {
        limit: pageSize,
        skip: (page - 1) * pageSize,
      };
      const response = await ShopServ.getSellerShops(params);

      const shopsData = response?.data || [];
      setShops(shopsData);
      setTotalShops(response?.total || shopsData.length);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch shops!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
  }, []);

  // Fetch categories for filter
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await CategoryServ.index({ limit: 'all' });
        let data = res?.data || res;
        if (!Array.isArray(data)) data = data.data || data.categories || data.items || [];
        setCategories(data.filter(c => !c.parentId).map(c => ({
          value: (c._id || c.id)?.toString(),
          label: c.name,
        })));
      } catch {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  const handlePageChange = (page) => setPage(page);
  const onShowSizeChange = (current, size) => setPageSize(size);



  const handleEditShop = (record) => {
    setShopDetails(record);
    setIsEditing(true);
    setAddShop(true);
  };

  const handleDeleteShop = async (record) => {
    try {
      await ShopServ.remove(record._id);
      message.success("Shop removed successfully!");
      fetchShops();
    } catch (error) {
      console.error(error);
      message.error("Failed to remove shop!");
    }
  };

  const refreshShopSettings = (isRefresh) => {
    setAddShop(false);
    if (isRefresh) fetchShops();
  };

  const handleReset = () => {
    setSearchText("");
    setCategoryFilter(null);
  };

  const filteredData = (Array.isArray(shops) ? shops : []).filter((item) => {
    let matches = (item.name || item.shopName)?.toLowerCase().includes(searchText.toLowerCase());
    if (matches && categoryFilter) {
      const shopCatId = (item.business?.categoryId?._id || item.business?.categoryId?.id || item.business?.categoryId)?.toString();
      if (shopCatId !== categoryFilter) matches = false;
    }
    return matches;
  });

  const columns = [
    {
      title: "Image",
      dataIndex: "imageUrl",
      key: "imageUrl",
      width: 70,
      align: "center",
      render: (url) => <TableImage src={getImageUrl(url)} alt="shop" />,
    },
    { 
      title: "Shop Name", 
      dataIndex: "name", 
      key: "name", 
      render: (name) => <span style={{ fontWeight: 600, color: '#1a1a2e' }}>{name}</span>,
    },
    {
      title: "Shop ID",
      dataIndex: "shopId",
      key: "shopId",
      width: 130,
      render: (id) => <span style={{ color: '#888', fontSize: 13 }}>{id}</span>
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (loc) => {
        const fullText = loc ? `${loc.address || ''}, ${loc.city || ''}` : "N/A";
        return <TextWithPopover text={fullText} length={30} title="Location" />;
      }
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => <TextWithPopover text={text} length={30} title="Description" />
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      align: "center",
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            type="link" 
            size="small"
            style={{ borderRadius: 6 }}
            onClick={() => handleEditShop(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Remove this shop?"
            onConfirm={() => handleDeleteShop(record)}
            okText="Yes"
            cancelText="No"
            placement="left"
            okButtonProps={{ danger: true, style: { borderRadius: '6px' } }}
            cancelButtonProps={{ style: { borderRadius: '6px' } }}
          >
            <Button 
              icon={<DeleteOutlined />} 
              type="link" 
              danger
              size="small"
              style={{ borderRadius: 6 }}
            > Remove </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="pcu-padding-24">
      <div className="pcu-margin-bottom-16">
        <Breadcrumbs />
      </div>

      <div className="main-content-card pcu-bg-white pcu-radius-16 pcu-shadow-sm pcu-overflow-hidden">
        <div className="pcu-padding-x-24 pcu-padding-top-24 pcu-padding-bottom-0 pcu-border-bottom">
          <h1 className="pcu-font-22 pcu-font-700 pcu-color-title pcu-margin-bottom-16">
            Shop Settings
          </h1>
        </div>

        <div className="pcu-padding-24">
          <FilterBar>
            <Col xs={24} md={10}>
              <SearchInput
                label="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by shop name..."
              />
            </Col>
            <Col xs={24} md={10}>
              <FormSelect
                label="Category"
                placeholder="All Categories"
                value={categoryFilter}
                onChange={setCategoryFilter}
                options={categories}
                showSearch
                allowClear
              />
            </Col>
            <Col xs={24} md={4} style={{ textAlign: 'right' }}>
              <ResetButton onClick={handleReset} />
            </Col>
          </FilterBar>



          <div className="pcu-border-top pcu-margin-x-n24">
            <DataTable
              columns={columns}
              data={filteredData}
              rowKey="_id"
              loading={loading}
              pagination={{
                current: page,
                pageSize: pageSize,
                total: totalShops,
                onChange: handlePageChange,
                onShowSizeChange: onShowSizeChange
              }}
            />
          </div>

          {/* Add/Edit Shop Modal */}
          {addShop && (
            <AddShop
              open={addShop}
              shopDetails={shopDetails}
              isEditing={isEditing}
              refreshShops={refreshShopSettings}
            />
          )}
      </div>
    </div>
  </div>
);
};

export default ShopSetting;

